<template>
  <div v-show="visible" class="fullscreen-sign-overlay">
    <!-- 主容器 -->
    <div class="main-container">
      <!-- 标题区域 -->
      <div class="rotated-header c_title">
        <h3>请在下方空白处手写签名</h3>
      </div>

      <!-- Canvas 区域 -->
      <div class="canvas-box">
        <canvas ref="signCanvas"></canvas>
      </div>

      <!-- 按钮区域 -->
      <div class="rotated-header c_button">
        <div class="btn-group">
          <button class="btn-clear" @click="close">关闭</button>
          <button class="btn-clear" @click="clear">重写</button>
          <button class="btn-submit" @click="confirm">确认并返回</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch, Ref } from "vue-property-decorator";

@Component({
  // name: "SignaturePad",
  // emits: ["confirm", "update:visible"], // Vue2中使用 $emit，此处仅作类型提示或配合插件
})
export default class SignaturePad extends Vue {
  @Prop({ type: Boolean, default: false }) visible!: boolean;

  @Ref("signCanvas") readonly signCanvas!: HTMLCanvasElement;

  ctx: CanvasRenderingContext2D | null = null;
  isDrawing = false;
  dpr = window.devicePixelRatio || 1;

  @Watch("visible")
  onVisibleChange(val: boolean) {
    if (val) {
      this.$nextTick(() => {
        this.initCanvas();
      });
    }
  }

  initCanvas() {
    const canvas = this.signCanvas;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    const cssWidth = parent.clientWidth;
    const cssHeight = parent.clientHeight;

    // 设置 Canvas 实际像素
    canvas.width = cssWidth * this.dpr;
    canvas.height = cssHeight * this.dpr;

    // 设置 CSS 显示尺寸
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    // 初始化 Context
    const ctx = canvas.getContext("2d");
    if (ctx) {
      this.ctx = ctx;
      this.ctx.scale(this.dpr, this.dpr);
      this.ctx.lineWidth = 3;
      this.ctx.lineCap = "round";
      this.ctx.strokeStyle = "#000000";
      this.bindEvents(canvas);
    }
  }

  bindEvents(canvas: HTMLCanvasElement) {
    // 鼠标事件
    canvas.addEventListener("mousedown", this.startDraw);
    canvas.addEventListener("mousemove", this.draw);
    window.addEventListener("mouseup", this.endDraw);

    // 触摸事件 (H5核心)
    canvas.addEventListener(
      "touchstart",
      (e: TouchEvent) => {
        e.preventDefault();
        if (e.changedTouches.length > 0) {
          this.startDraw(e.changedTouches[0]);
        }
      },
      { passive: false }
    );

    canvas.addEventListener(
      "touchmove",
      (e: TouchEvent) => {
        e.preventDefault();
        if (e.changedTouches.length > 0) {
          this.draw(e.changedTouches[0]);
        }
      },
      { passive: false }
    );

    canvas.addEventListener("touchend", this.endDraw);
  }

  getPos(e: MouseEvent | Touch) {
    const canvas = this.signCanvas;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    return { x, y };
  }

  startDraw(e: MouseEvent | Touch) {
    this.isDrawing = true;
    const pos = this.getPos(e);
    if (this.ctx) {
      this.ctx.beginPath();
      this.ctx.moveTo(pos.x, pos.y);
    }
  }

  draw(e: MouseEvent | Touch) {
    if (!this.isDrawing || !this.ctx) return;
    const pos = this.getPos(e);
    this.ctx.lineTo(pos.x, pos.y);
    this.ctx.stroke();
  }

  endDraw() {
    this.isDrawing = false;
    if (this.ctx) {
      this.ctx.closePath();
    }
  }

  clear() {
    const canvas = this.signCanvas;
    if (this.ctx && canvas) {
      this.ctx.clearRect(
        0,
        0,
        canvas.width / this.dpr,
        canvas.height / this.dpr
      );
    }
  }

  close() {
    this.$emit("update:visible", false);
  }

  /**
   * 旋转 Base64 图片工具方法
   */
  rotateBase64Img(src: string, edg: number, callback: (base64: string) => void) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let imgW: number; // 图片宽度
    let imgH: number; // 图片高度
    let size: number; // canvas初始大小

    if (edg % 90 !== 0) {
      console.error("旋转角度必须是90的倍数!");
      return;
    }
    
    edg < 0 && (edg = (edg % 360) + 360);
    const quadrant = (edg / 90) % 4; // 旋转象限
    const cutCoor = { sx: 0, sy: 0, ex: 0, ey: 0 }; // 裁剪坐标
    
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = src;
    
    image.onload = () => {
      imgW = image.width;
      imgH = image.height;
      size = imgW > imgH ? imgW : imgH;
      
      canvas.width = size * 2;
      canvas.height = size * 2;
      
      switch (quadrant) {
        case 0:
          cutCoor.sx = size;
          cutCoor.sy = size;
          cutCoor.ex = size + imgW;
          cutCoor.ey = size + imgH;
          break;
        case 1:
          cutCoor.sx = size - imgH;
          cutCoor.sy = size;
          cutCoor.ex = size;
          cutCoor.ey = size + imgW;
          break;
        case 2:
          cutCoor.sx = size - imgW;
          cutCoor.sy = size - imgH;
          cutCoor.ex = size;
          cutCoor.ey = size;
          break;
        case 3:
          cutCoor.sx = size;
          cutCoor.sy = size - imgW;
          cutCoor.ex = size + imgH;
          cutCoor.ey = size + imgW;
          break;
      }
      
      ctx.translate(size, size);
      ctx.rotate((edg * Math.PI) / 180);
      ctx.drawImage(image, 0, 0);
      
      const imgData = ctx.getImageData(
        cutCoor.sx,
        cutCoor.sy,
        cutCoor.ex - cutCoor.sx, // 修正：getImageData需要宽度和高度
        cutCoor.ey - cutCoor.sy
      );
      
      if (quadrant % 2 === 0) {
        canvas.width = imgW;
        canvas.height = imgH;
      } else {
        canvas.width = imgH;
        canvas.height = imgW;
      }
      
      ctx.putImageData(imgData, 0, 0);
      callback(canvas.toDataURL());
    };
  }

  confirm() {
    const originalCanvas = this.signCanvas;
    if (!originalCanvas) return;

    this.rotateBase64Img(
      originalCanvas.toDataURL("image/png"),
      0, // 这里传入0代表不旋转，如果需要旋转可以传 90, 180, 270
      (rotatedBase64: string) => {
        console.log("处理后的Base64:", rotatedBase64);
        this.$emit("confirm", rotatedBase64);
        this.$emit("update:visible", false);
      }
    );
  }
}
</script>

<style lang="scss" scoped>
/* 遮罩层：全屏，绝对不旋转 */
.fullscreen-sign-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 主容器：竖屏布局 */
.main-container {
  width: 90vw;
  height: 500px;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: relative;
}

.c_title {
  /* 根据需要调整位置 */
}

.c_button {
  /* 根据需要调整位置 */
}

/* 旋转区域 */
.rotated-header {
  transform-origin: center center;
  margin-bottom: 20px;
  white-space: nowrap;
}

.rotated-header h3 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 18px;
}

.btn-group {
  display: flex;
  gap: 15px;
}

button {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: bold;
  color: #fff;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}

.btn-clear {
  background: #ff4d4f;
}

.btn-submit {
  background: #52c41a;
}

/* Canvas 区域 */
.canvas-box {
  border: 2px dashed #999;
  background: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  width: 70%;
  margin-left: 50px;
  height: 200px;
}

canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
}
</style>